// DashboardFeedback.jsx
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Row, Col, Button, Statistic, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import FeedbackList from "./FeedbackList";
import FeedbackModal from "./FeedbackModal";
import DeleteModal from "./DeleteModal";
import useFeedbackFormService from "../../../../services/feedbackFormService";
import { useNavigate } from "react-router-dom";
import useFeedbackService from "../../../../services/feedbackService";
import { useAuth } from "../../../../context/authContext";
import { useLanguage } from "../../../../context/LanguageContext";
import dashboardContent from "../../../../utils/dashboardContent";

const DashboardFeedback = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const [formToDelete, setFormToDelete] = useState(null);
  const [forms, setForms] = useState([]);
  const [feedbackCount, setFeedbackCount] = useState("0");
  const [averageRating, setAverageRating] = useState("0");
  const { deleteFeedbackForm, getFeedbackForms } = useFeedbackFormService();
  const { getFeedbackDashboardMetricData } = useFeedbackService();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, fetchUser } = useAuth();
  const { language } = useLanguage();
  const text = dashboardContent[language].feedback;

  const handleModalClose = () => {
    setIsModalVisible(false);
    setCurrentForm(null); // altes Formular rausnehmen
    setIsEditing(false); // Bearbeitungsmodus zurücksetzen
  };
  useEffect(() => {
    if (location.state?.openModal && forms.length > 0) {
      const activeCount = forms.filter((f) => f.status === "active").length;
      if (activeCount < user.reportLimits.feedbackCreation) {
        setIsModalVisible(true);
      } else {
        message.warning("Limit erreicht");
      }
      // Hier das Flag zurücksetzen:
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, forms, user.reportLimits.feedbackCreation]);

  const handleCreate = () => {
    setIsEditing(false);
    setIsModalVisible(true);
    setCurrentForm(null);
  };

  const handleEditForm = (id) => {
    const formToEdit = forms.find((form) => form._id === id);
    if (!formToEdit) return;

    setCurrentForm(formToEdit);
    setIsEditing(true);
    setIsModalVisible(true);
  };

  const handleDeleteModalOpen = (formId) => {
    setFormToDelete(formId);
    setDeleteModalVisible(true);
  };

  const handleDeleteForm = async () => {
    if (!formToDelete) return;
    try {
      await deleteFeedbackForm(formToDelete);
      setForms(forms.filter((form) => form._id !== formToDelete));
      message.success(text.messages.deleteSuccess);
    } catch (error) {
      console.error("Fehler beim Löschen des Formulars:", error.message);
      message.error(text.messages.deleteError);
      await fetchUser();
    } finally {
      setDeleteModalVisible(false);
      setFormToDelete(null);
    }
  };

  const fetchForms = async () => {
    try {
      const data = await getFeedbackForms();
      setForms(data);
    } catch (error) {
      message.error("Fehler beim Laden der Formulare");
      console.error(error);
    }
  };

  const handleFormSubmit = async () => {
    await fetchForms(); // 🔁 alles neu laden
    setIsModalVisible(false);
    setCurrentForm(null);
  };

  const handleViewFeedbacks = (id) => {
    const url = `/feedbackform/${id}?viewmode=readonly`;
    console.log("Navigating to:", url);
    navigate(url);
  };

  useEffect(() => {
    const getAllFeedback = async () => {
      const metricData = await getFeedbackDashboardMetricData();
      setFeedbackCount(metricData.feedbackCount);
      setAverageRating(metricData.avgRating);
    };

    getAllFeedback();
  }, []);

  const statistics = (
    <div style={{ marginBottom: "20px", textAlign: "center" }}>
      <Row gutter={[16, 16]} justify="center">
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title={text.stats.activeForms}
            value={
              forms.filter((form) => form.status === "active").length +
              "/" +
              user.reportLimits.feedbackCreation
            }
            valueStyle={{ color: "#3f8600" }}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title={text.stats.totalFeedback}
            value={feedbackCount}
            valueStyle={{ color: "#1890ff" }}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title={text.stats.averageRating}
            value={averageRating || 0}
            suffix="/ 5"
            valueStyle={{ color: "#cf1322" }}
          />
        </Col>
      </Row>
    </div>
  );

  return (
    <div style={{ padding: "20px" }}>
      {statistics}

      <FeedbackList
        forms={forms}
        setForms={setForms}
        onEdit={handleEditForm}
        onDelete={handleDeleteModalOpen}
        onView={handleViewFeedbacks}
      />

      <div style={{ marginTop: "30px", textAlign: "center" }}>
        <Button
          type="primary"
          size="large"
          shape="round"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          className="bg-orange-500"
          disabled={
            forms.filter((form) => form.status === "active").length >=
            user.reportLimits.feedbackCreation
          }
        >
          {text.button}
        </Button>
      </div>

      <FeedbackModal
        visible={isModalVisible}
        isEditing={isEditing}
        currentForm={currentForm}
        onClose={handleModalClose}
        onSubmit={handleFormSubmit}
        forms={forms}
      />

      <DeleteModal
        visible={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        onConfirm={handleDeleteForm}
      />
    </div>
  );
};

export default DashboardFeedback;
